/**
 * Authentication Store
 * Manages authentication state using Zustand
 * 
 * @author Varun Pandey
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import type { User } from '@/api/types';
import { resetAllStores } from '@/stores/resetAllStores';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  
  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  clearTokens: () => Promise<void>;
  logout: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  markAuthenticatedFromSession: (role?: User['role']) => void;
  /** Restore in-memory accessToken from secure storage after app reload. */
  hydrateAccessTokenFromSecureStorage: () => Promise<string | null>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      accessToken: null,
      refreshToken: null,

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
        if (user) {
          storageManager.set(STORAGE_KEYS.USER_DATA, user);
        }
      },

      setTokens: async (accessToken, refreshToken) => {
        await storageManager.set(STORAGE_KEYS.ACCESS_TOKEN, accessToken, true);
        await storageManager.set(STORAGE_KEYS.REFRESH_TOKEN, refreshToken, true);
        set({ accessToken, refreshToken });
      },

      clearTokens: async () => {
        await storageManager.remove(STORAGE_KEYS.ACCESS_TOKEN, true);
        await storageManager.remove(STORAGE_KEYS.REFRESH_TOKEN, true);
        set({ accessToken: null, refreshToken: null });
      },

      logout: async () => {
        await get().clearTokens();
        await storageManager.remove(STORAGE_KEYS.USER_DATA);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(STORAGE_KEYS.LOGIN_REDIRECT_MESSAGE);
          sessionStorage.removeItem(STORAGE_KEYS.EVENT_ESSENTIALS_ACTIVE_TAB);
        }
        resetAllStores();
        set({
          user: null,
          isAuthenticated: false,
          accessToken: null,
          refreshToken: null,
        });
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('auth-storage');
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),
      markAuthenticatedFromSession: (role = 'artist') => {
        const existingUser = get().user;
        if (existingUser) {
          set({ isAuthenticated: true });
          return;
        }

        // Cookie-auth session exists but user profile isn't hydrated yet.
        // Seed minimal user so route guards and public guards keep session alive.
        const sessionUser: User = {
          id: '',
          email: '',
          name: '',
          role,
          createdAt: '',
          updatedAt: '',
        };
        set({ user: sessionUser, isAuthenticated: true });
      },

      hydrateAccessTokenFromSecureStorage: async () => {
        const current = get().accessToken?.trim();
        if (current) return current;

        const stored = await storageManager.get<string>(STORAGE_KEYS.ACCESS_TOKEN, true);
        const token = typeof stored === 'string' ? stored.trim() : '';
        if (token) {
          set({ accessToken: token });
          return token;
        }
        return null;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
      }),
    }
  )
);
